if (typeof TextMediaScroller === "undefined") {
  class TextMediaScroller extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = -1;
      this.items = [];
      this.reducedMotion = false;
      this.mobileQuery = null;
      this.scrollRaf = 0;
      this.boundSelect = this.onBlockSelect.bind(this);
      this.boundTabClick = this.onTabClick.bind(this);
      this.boundModeChange = this.onModeChange.bind(this);
      this.boundScroll = this.onScroll.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll("[data-scroller-item]"));

      if (!this.items.length) return;

      this.reducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.mobileQuery =
        typeof window.matchMedia === "function"
          ? window.matchMedia("(max-width: 767px)")
          : null;

      this.items.forEach((item) => {
        const tab = item.querySelector("[data-scroller-tab]");
        if (tab) tab.addEventListener("click", this.boundTabClick);
      });

      this.mobileQuery?.addEventListener?.("change", this.boundModeChange);
      this.setupMode();

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener("shopify:block:select", this.boundSelect);
      }
    }

    disconnectedCallback() {
      this.teardownDesktopScroll();
      this.mobileQuery?.removeEventListener?.("change", this.boundModeChange);
      this.items.forEach((item) => {
        const tab = item.querySelector("[data-scroller-tab]");
        if (tab) tab.removeEventListener("click", this.boundTabClick);
      });
      document.removeEventListener("shopify:block:select", this.boundSelect);
    }

    isMobile() {
      return Boolean(this.mobileQuery?.matches);
    }

    onModeChange() {
      this.setupMode();
    }

    setupMode() {
      this.teardownDesktopScroll();

      if (!this.isMobile()) {
        // Desktop: sticky tabs — track topmost visible for video play/pause
        let idx = this.items.findIndex((el) =>
          el.classList.contains("is-active")
        );
        if (idx < 0) idx = this.activeIndex >= 0 ? this.activeIndex : 0;
        this.setActive(idx, { force: true });
        this.setupDesktopScroll();
        this.pickActiveFromScroll();
        return;
      }

      // Mobile: multi-open accordion. Open first only if nothing is open.
      const anyOpen = this.items.some((el) =>
        el.classList.contains("is-active")
      );
      if (!anyOpen) {
        this.setMobileOpen(0, true);
      } else {
        this.items.forEach((el) => {
          const open = el.classList.contains("is-active");
          this.syncMedia(
            el.querySelector("[data-scroller-item-media]"),
            open
          );
        });
      }
    }

    setupDesktopScroll() {
      window.addEventListener("scroll", this.boundScroll, { passive: true });
      window.addEventListener("resize", this.boundScroll, { passive: true });
    }

    teardownDesktopScroll() {
      window.removeEventListener("scroll", this.boundScroll);
      window.removeEventListener("resize", this.boundScroll);
      if (this.scrollRaf) {
        cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = 0;
      }
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = 0;
        this.pickActiveFromScroll();
      });
    }

    /**
     * Sticky stack: prefer the lowest (topmost painted) item that still covers
     * the upper half of the viewport — videos only play on that tab.
     */
    pickActiveFromScroll() {
      if (!this.items.length || this.isMobile()) return;

      const probeY = window.innerHeight * 0.35;
      let bestIndex = this.activeIndex >= 0 ? this.activeIndex : 0;

      for (let i = this.items.length - 1; i >= 0; i -= 1) {
        const rect = this.items[i].getBoundingClientRect();
        if (rect.top <= probeY && rect.bottom > probeY) {
          bestIndex = i;
          break;
        }
      }

      this.setActive(bestIndex);
    }

    onTabClick(event) {
      if (!this.isMobile()) return;
      const item = event.currentTarget.closest("[data-scroller-item]");
      if (!item) return;
      const index = this.items.indexOf(item);
      if (index < 0) return;

      const open = !item.classList.contains("is-active");
      this.setMobileOpen(index, open);
    }

    setMobileOpen(index, open) {
      const item = this.items[index];
      if (!item) return;

      item.classList.toggle("is-active", open);
      item.setAttribute("aria-current", open ? "true" : "false");

      const tab = item.querySelector("[data-scroller-tab]");
      if (tab) tab.setAttribute("aria-expanded", open ? "true" : "false");

      this.syncMedia(item.querySelector("[data-scroller-item-media]"), open);

      if (open) {
        this.activeIndex = index;
      } else if (this.activeIndex === index) {
        this.activeIndex = this.items.findIndex((el) =>
          el.classList.contains("is-active")
        );
      }

      this.dispatchEvent(
        new CustomEvent("text-media-scroller:change", {
          bubbles: true,
          detail: {
            index,
            open,
            bgColor: item.dataset?.bgColor || "",
          },
        })
      );
    }

    syncMedia(el, active) {
      el?.querySelectorAll?.("video").forEach((video) => {
        if (active) {
          video.play?.().catch(() => {});
        } else {
          video.pause?.();
        }
      });
    }

    /**
     * Desktop exclusive active tab (video focus while pinned/stacking).
     */
    setActive(index, { force = false } = {}) {
      if (!force && index === this.activeIndex) return;
      if (index < 0 || index >= this.items.length) return;

      this.activeIndex = index;
      const item = this.items[index];
      const bgColor = item?.dataset?.bgColor || "";

      this.items.forEach((el, i) => {
        const active = i === index;
        el.classList.toggle("is-active", active);
        el.setAttribute("aria-current", active ? "true" : "false");

        const tab = el.querySelector("[data-scroller-tab]");
        if (tab) tab.setAttribute("aria-expanded", active ? "true" : "false");

        this.syncMedia(
          el.querySelector("[data-scroller-item-media]"),
          active && !this.isMobile()
        );
      });

      this.dispatchEvent(
        new CustomEvent("text-media-scroller:change", {
          bubbles: true,
          detail: { index, bgColor },
        })
      );
    }

    onBlockSelect(event) {
      if (!event.target || !this.contains(event.target)) return;
      const item = event.target.closest("[data-scroller-item]");
      if (!item) return;
      const index = this.items.indexOf(item);
      if (index < 0) return;

      if (this.isMobile()) {
        this.setMobileOpen(index, true);
        return;
      }

      this.setActive(index, { force: true });
      item.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  if (!window.customElements.get("text-media-scroller")) {
    window.customElements.define("text-media-scroller", TextMediaScroller);
  }
}
